/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { forwardRef, memo, type PropsWithChildren, type Ref } from 'react';

import { css, jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import {
	TAB_HEIGHT,
	TAB_UNDERLINE_HEIGHT,
	TAB_UNDERLINE_HEIGHT_OLD,
	UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR,
} from '@atlassian/navigation-kit-common';

const TabWrapper = memo(
	forwardRef<
		HTMLDivElement,
		PropsWithChildren<{
			isSelected?: boolean;
			isDragging?: boolean;
		}>
	>(({ children, isSelected = false, isDragging = false }, ref: Ref<HTMLDivElement>) => {
		if (fg('blu-6305_tab_item_styling_in_horizontal_navigation')) {
			return (
				<div css={containerStyles}>
					<div
						css={[
							wrapperTabStyles,
							isDragging && draggingTabStyles,
							isSelected && selectedWrapperTabStyles,
						]}
						ref={ref}
					>
						{children}
					</div>
				</div>
			);
		}

		return (
			<div
				css={[
					wrapperTabStylesOld,
					isSelected && themedUnderlineOld,
					isDragging && draggingTabStyles,
				]}
				ref={ref}
			>
				{children}
			</div>
		);
	}),
);

const themedUnderlineOld = css({
	'&::after': {
		position: 'absolute',
		left: token('space.0', '0'),
		right: token('space.0', '0'),
		bottom: token('space.0', '0'),
		top: 'auto',
		content: "''",
		height: token('space.025', '2px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		borderRadius: `${TAB_UNDERLINE_HEIGHT_OLD / 2}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		backgroundColor: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR}, ${token('color.link')})`,
	},
});

const wrapperTabStylesOld = css({
	display: 'flex',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	blockSize: `${TAB_HEIGHT}px`,
	boxSizing: 'border-box',
	minInlineSize: 'max-content',
	alignItems: 'center',
	flexDirection: 'row-reverse',
	outlineOffset: token('space.negative.025'),
	borderStartStartRadius: token('border.radius.100'),
	borderStartEndRadius: token('border.radius.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:has([data-delegated-focus-ring]:focus-visible)': {
		outline: `2px solid ${token('color.border.focused')}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:hover, &:has(:focus-visible)': {
		'&::after': {
			position: 'absolute',
			left: token('space.0', '0'),
			right: token('space.0', '0'),
			bottom: token('space.0', '0'),
			top: 'auto',
			content: "''",
			height: token('space.025', '2px'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			borderRadius: `${TAB_UNDERLINE_HEIGHT_OLD / 2}px`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			backgroundColor: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR}, ${token('color.link')})`,
		},
	},
});

const wrapperTabStyles = css({
	paddingBlock: token('space.050'),
	paddingLeft: token('space.075'),
	paddingRight: token('space.075'),
	display: 'flex',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	blockSize: `${TAB_HEIGHT}px`,
	boxSizing: 'border-box',
	minInlineSize: 'max-content',
	alignItems: 'center',
	flexDirection: 'row-reverse',
	outlineOffset: token('space.negative.025'),
	borderRadius: token('border.radius.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:has([data-delegated-focus-ring]:focus-visible)': {
		outline: `2px solid ${token('color.border.focused')}`,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:hover': {
		'&::after': {
			position: 'absolute',
			left: token('space.075', '6px'),
			right: token('space.075', '6px'),
			bottom: token('space.0', '0px'),
			top: 'auto',
			content: "''",
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			height: `${TAB_UNDERLINE_HEIGHT}px`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			backgroundColor: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR}, ${token('color.link')})`,
			zIndex: -1,
		},
	},
});

const selectedWrapperTabStyles = css({
	'&::after': {
		position: 'absolute',
		left: token('space.075', '6px'),
		right: token('space.075', '6px'),
		bottom: token('space.0', '0px'),
		top: 'auto',
		content: "''",
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		height: `${TAB_UNDERLINE_HEIGHT}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		backgroundColor: `var(${UNSAFE_HORIZONTAL_NAVIGATION_COLOR_LINK_VAR}, ${token('color.link')})`,
		zIndex: -1,
	},
});

/*
	This container is used to ensure that the outline is above the tab underline by creating a new stacking context
	around the tab wrapper (which has the ::after pseudo-element inside it).
*/
const containerStyles = css({
	position: 'relative',
	zIndex: 1,
});

const draggingTabStyles = css({
	opacity: 0.1,
});

export default TabWrapper;
